import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DestroyRef,
    HostBinding,
    OnChanges,
    OnDestroy,
    inject,
    input,
    output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { EventParticipantImage } from '@frontend/sports/betting-offer/feature/model';
import { EpcotConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { isDefined } from '@frontend/sports/common/core/utils/extended-types';
import { ISimpleChanges } from '@frontend/sports/common/core/utils/simple-change';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { FavouriteType, FavouritesService, FavouritesViewModel } from '@frontend/sports/favourites/core/feature';
import { RedirectHelperService } from '@frontend/sports/navigation/core/feature/redirect';
import { TrackData } from '@frontend/sports/tracking/feature';
import { UserService } from '@frontend/sports/user/feature';
import { DsButtonIcon } from '@frontend/ui/button-icon';
import { DsToastType } from '@frontend/ui/toast';
import { IconCustomComponent } from '@frontend/vanilla/shared/icons';
import { Observable, Subscription, of } from 'rxjs';
import { catchError, filter, map } from 'rxjs';

import { FavouriteToggleTrackingService } from './favourite-toggle-tracking.service';

export interface FavouriteToggleTracking {
    name: string;
    source: string;
    position?: number;
    additional?: Partial<TrackData>;
}

@Component({
    selector: 'ms-favourite-toggle',
    templateUrl: './favourite-toggle.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [IconCustomComponent, DsButtonIcon],
})
export class FavouriteToggleComponent implements OnChanges, OnDestroy {
    id = input.required<string>();
    name = input.required<string>();
    sport = input.required<number>();
    favIconSize = input.required<string>();
    type = input.required<FavouriteType>();
    static = input<boolean>();
    tracking = input.required<FavouriteToggleTracking>();
    participantImage = input<EventParticipantImage>();
    toggle = output<boolean>();
    @HostBinding('class') className = 'favourite-toggle';

    model?: FavouritesViewModel;
    disabled: boolean;
    toggleClass: boolean;
    flag = false;
    private isEpcotEnabled: boolean;
    private subscription?: Subscription;
    private readonly destroyRef = inject(DestroyRef);
    private readonly sitecore = inject(Sitecore);
    private readonly userService = inject(UserService);
    private readonly favouritesService = inject(FavouritesService);
    private readonly redirectHelper = inject(RedirectHelperService);
    private readonly changeRef = inject(ChangeDetectorRef);
    private readonly trackingService = inject(FavouriteToggleTrackingService);
    private readonly epcotConfig = inject(EpcotConfig);

    constructor() {
        this.isEpcotEnabled = this.epcotConfig.isEnabled;
    }

    setToggle(event: MouseEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.flag = true;
        if (this.userService.isAuthenticated) {
            if (this.model) {
                this.remove();
            } else {
                this.add();
            }
        } else {
            this.redirectHelper.goToLogin();
        }
    }

    ngOnChanges(changes: ISimpleChanges<FavouriteToggleComponent>): void {
        if (changes.static) {
            if (this.static()) {
                this.unsubscribe();
            } else {
                this.subscribe();
            }
        }

        this.setState();
    }

    ngOnDestroy(): void {
        this.unsubscribe();
    }

    private unsubscribe(): void {
        if (this.subscription) {
            this.subscription.unsubscribe();
            this.subscription = undefined;
        }
    }

    private subscribe(): void {
        if (!this.subscription) {
            this.subscription = this.favouritesService.change.subscribe(() => this.setState());
        }
    }

    private remove(): void {
        if (!this.model) {
            return;
        }
        this.toggleClass = false;
        this.favouritesService
            .remove(this.model)
            .pipe(this.handleError(), takeUntilDestroyed(this.destroyRef))
            .subscribe(() =>
                this.toggled(interpolateDoubleBracesString(this.sitecore.favouritesMessages.RemoveSuccess, { name: this.name() }), false, 'success'),
            );
    }

    private add(): void {
        this.toggleClass = true;
        this.favouritesService
            .add(this.id(), this.sport(), this.type(), this.name(), this.participantImage())
            .pipe(this.handleError(), takeUntilDestroyed(this.destroyRef))
            .subscribe(() =>
                this.toggled(
                    interpolateDoubleBracesString(this.sitecore.favouritesMessages.AddSuccess, { name: this.name() }),
                    false,
                    this.isEpcotEnabled ? 'info' : 'success',
                ),
            );
    }

    private handleError = () => (source: Observable<unknown>) =>
        source.pipe(
            map(() => true),
            //eslint-disable-next-line  @typescript-eslint/no-explicit-any
            catchError((error: any) => {
                this.toggleClass = !!this.model;
                this.toggled(error.message, true);
                return of(false);
            }),
            filter((result) => result),
        );

    private toggled(message: string, toggleError = false, type: DsToastType = 'info'): void {
        this.favouritesService.notify(message, type);
        this.setState();

        const isToggled = isDefined(this.model);
        this.toggle.emit(isToggled);
        this.changeRef.markForCheck();
        if (!toggleError) {
            this.trackingService.trackToggle(isToggled, this.type(), this.tracking());
        }
    }

    private setState(): void {
        this.disabled = !this.userService.isAuthenticated;
        this.model = this.favouritesService.get(this.id(), this.sport(), this.type());
        if (!this.flag) {
            this.toggleClass = !!this.model;
        }

        this.changeRef.markForCheck();
    }
}
